/**
 * @component NotificationSettings
 * UI-only redesign to match mockups. Backend logic unchanged.
 */

import {
  Box,
  Field,
  Skeleton,
  SwitchControl,
  SwitchHiddenInput,
  SwitchRoot,
  SwitchThumb,
  SwitchThumbIndicator,
  Text,
  VStack,
  HStack,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { HiCheck, HiX } from 'react-icons/hi'
import { getNotificationSettings, updateNotificationSettings } from '@/api/settings.api'
import { SettingsActionBar } from '@/pages/settings/SettingsActionBar'
import { DismissChangesDialog } from '@/components/modals/DismissChangesDialog'
import { useUnsavedChangesGuardCustom } from '@/hooks/useUnsavedChangesGuard'
import { FormLayout } from '@/components/layout/FormLayout'

function SwitchRow(props: {
  title: string
  description: string
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}) {
  const { title, description, checked, disabled, onChange } = props

  return (
    <HStack gap={4} align="flex-start" w="full">
      <SwitchRoot
        checked={checked}
        disabled={disabled}
        colorPalette="green"
        onCheckedChange={(detail: { checked: boolean }) => onChange(detail.checked)}
      >
        <SwitchHiddenInput />
        <SwitchControl>
          <SwitchThumb>
            <SwitchThumbIndicator fallback={<HiX />}>
              <HiCheck />
            </SwitchThumbIndicator>
          </SwitchThumb>
        </SwitchControl>
      </SwitchRoot>

      <Field.Root>
        <Field.Label>{title}</Field.Label>
        <Field.HelperText>{description}</Field.HelperText>
      </Field.Root>
    </HStack>
  )
}

export function NotificationSettings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // explicit cancel dialog (action bar cancel button)
  const [cancelOpen, setCancelOpen] = useState(false)

  const [emailEnabled, setEmailEnabled] = useState(false)
  const [inAppEnabled, setInAppEnabled] = useState(false)

  const [original, setOriginal] = useState({
    emailEnabled: false,
    inAppEnabled: false,
  })

  const dirty = useMemo(
    () => emailEnabled !== original.emailEnabled || inAppEnabled !== original.inAppEnabled,
    [emailEnabled, inAppEnabled, original]
  )

  // ✅ route navigation guard (same as OrderCapacitySettings)
  const { isOpen, confirmNavigation, cancelNavigation } = useUnsavedChangesGuardCustom(dirty)

  useEffect(() => {
    async function load() {
      try {
        setLoading(true)
        const res = await getNotificationSettings()

        const nextEmail = res.areEmailNotificationsEnabled ?? false
        const nextInApp = res.areInappNotificationsEnabled ?? false

        setEmailEnabled(nextEmail)
        setInAppEnabled(nextInApp)
        setOriginal({ emailEnabled: nextEmail, inAppEnabled: nextInApp })
      } catch {
        toast.error(
          'Benachrichtigungseinstellungen konnten nicht geladen werden. Bitte versuchen Sie es später erneut.'
        )
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const resetToOriginal = useCallback(() => {
    setEmailEnabled(original.emailEnabled)
    setInAppEnabled(original.inAppEnabled)
  }, [original.emailEnabled, original.inAppEnabled])

  const doSave = useCallback(async () => {
    setSaving(true)

    const payload: any = {}
    if (emailEnabled !== original.emailEnabled) payload.areEmailNotificationsEnabled = emailEnabled
    if (inAppEnabled !== original.inAppEnabled) payload.areInappNotificationsEnabled = inAppEnabled
    if (emailEnabled !== original.emailEnabled) payload.areEmailNotificationsEnabled = emailEnabled
    if (inAppEnabled !== original.inAppEnabled) payload.areInappNotificationsEnabled = inAppEnabled

    try {
      await updateNotificationSettings(payload)
      setOriginal({ emailEnabled, inAppEnabled })
      toast.success('Benachrichtigungseinstellungen wurden gespeichert.')
    } catch {
      toast.error(
        'Benachrichtigungseinstellungen konnten nicht gespeichert werden. Bitte versuchen Sie es erneut.'
      )
      throw new Error('save_failed')
    } finally {
      setSaving(false)
    }
  }, [emailEnabled, inAppEnabled, original.emailEnabled, original.inAppEnabled])

  return (
    <Box
      data-testid="notification-settings"
      position="relative"
      pb={dirty ? { base: '110px', md: '120px' } : 0}
    >
      {/* ✅ 1) Navigation-away dismiss dialog (same behavior as capacities) */}
      <DismissChangesDialog
        isOpen={isOpen}
        onClose={cancelNavigation}
        onDismiss={confirmNavigation}
        onSave={async () => {
          if (dirty) await doSave()
          confirmNavigation()
        }}
        onNavigate={cancelNavigation}
      />

      {/* ✅ 2) Explicit cancel button dismiss dialog */}
      <DismissChangesDialog
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onDismiss={() => {
          resetToOriginal()
          setCancelOpen(false)
        }}
        onSave={async () => {
          await doSave()
          setCancelOpen(false)
        }}
        onNavigate={() => {
          // kept for API compatibility
        }}
      />

      <FormLayout.Root>
        <FormLayout.Section>
          <FormLayout.Title>Benachrichtigungen</FormLayout.Title>
          <FormLayout.Description>
            Hier können Sie Benachrichtigungen aktivieren oder deaktivieren.
          </FormLayout.Description>
        </FormLayout.Section>

        {loading ? (
          <VStack align="start" gap={5}>
            <Skeleton height="22px" w="420px" />
            <Skeleton height="22px" w="520px" />
          </VStack>
        ) : (
          <VStack align="start" gap={8}>
            <SwitchRow
              title="Benachrichtigungen im SIB Portal"
              description="Hinweise und Updates innerhalb der Webanwendung."
              checked={inAppEnabled}
              disabled={saving}
              onChange={setInAppEnabled}
            />
            <SwitchRow
              title="Benachrichtigungen per E-Mail"
              description="Erhalten Sie Updates per E-Mail."
              checked={emailEnabled}
              disabled={saving}
              onChange={setEmailEnabled}
            />
          </VStack>
        )}

        <SettingsActionBar
          visible={dirty && !loading}
          onCancel={() => setCancelOpen(true)}
          cancelDisabled={saving}
          onSave={doSave}
          saveDisabled={saving}
          saving={saving}
        />
      </FormLayout.Root>
    </Box>
  )
}
